import { Component, inject, input, output, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { PlatformConfig, PlatformType } from '../../models/platform.model';
import { ButtonModule } from 'primeng/button';
import { PopoverModule } from 'primeng/popover';
import { DividerModule } from 'primeng/divider';
import { MenuItem } from 'primeng/api';
import { IconFieldModule } from 'primeng/iconfield';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { selectUserDetails } from 'src/app/store/user/auth/user-auth.selectors';
import { FboLocationService } from '../../services/fbo-location.service';
import { UserFboLocationsDto } from 'src/app/api/administration/models/user-fbo-locations-dto';

@Component({
  selector: 'app-platform-header',
  standalone: true,
  imports: [CommonModule, ButtonModule, PopoverModule, DividerModule, DatePipe, IconFieldModule],
  templateUrl: './platform-header.component.html',
  styleUrl: './platform-header.component.css',
})
export class PlatformHeader {
  private router = inject(Router);
  private store = inject(Store);
  private fboLocationService = inject(FboLocationService);

  config = input<PlatformConfig | null>(null);
  isSidebarPinned = input<boolean>(false);
  userDetails = toSignal(this.store.select(selectUserDetails));
  availablePlatforms = input<PlatformConfig[]>([]);
  platformChange = output<PlatformType>();
  logout = output<void>();
  menuToggle = output<void>();
  PlatformType = PlatformType;

  // Convert store observables to signals
  locations = toSignal(this.fboLocationService.locations$, { initialValue: [] });
  selectedLocation = toSignal(this.fboLocationService.selectedLocation$, { initialValue: null });
  locationLoading = toSignal(this.fboLocationService.isLoading$, { initialValue: false });
  locationSwitching = toSignal(this.fboLocationService.isSwitching$, { initialValue: false });

  currentTime = signal(new Date());
  localTimeZone = signal(
    Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value || 'LT'
  );

  constructor() {
    setInterval(() => {
      this.currentTime.set(new Date());
    }, 60000);
  }

  get platformMenuItems(): MenuItem[] {
    return this.availablePlatforms()
      .filter((p) => p.id !== this.config()?.id)
      .map((p) => ({
        label: p.id,
        title: p.name,
        icon: p.icon,
        command: () => this.platformChange.emit(p.id),
      }));
  }

  /**
   * Handle location selection and switch FBO location via store
   */
  selectLocation(location: UserFboLocationsDto) {
    if (location.id !== undefined) {
      this.fboLocationService.switchLocation(location.id);
    }
  }

  onLogoClick() {
    const route = this.config()?.defaultRoute;
    if (route) {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([route]);
      });
    }
  }
}
